import { DBricksConnection } from '../workflow/connections/databricks'
import {
  ColumnDataType,
  WorkflowComponentCodeParams,
  Provider,
  TableSchema,
  QueryAndOutputs,
  WorkflowCodeType,
  WorkflowComponentParamType,
  WorkflowComponentParameterMode,
  Language,
  WorkflowComponentOutputTablesParams
} from '../types'
import { WorkflowComponent } from './common/workflowcomponent'
import { dedent } from 'ts-dedent'
import { SINGLE_OUTPUT_NAME } from './common/singleoutputcomponent'

const expressionPlaceholders: Record<string, string> = {
  [Provider.BigQuery]: `{
    "mode_value": {
     "formula": "APPROX_TOP_COUNT(value, 1)[OFFSET(0)].value",
     "type": "String"
    },
    "conditional_count": {
     "formula": "COUNTIF(column = 'value') > 0",
     "type": "Boolean"
    }
  }`,
  [Provider.Snowflake]: `{
    "POPULATION_SUM": {
      "formula": "SUM(POPULATION)",
      "type": "Number"
    },
    "CITY_NAME": {
      "formula": "IFF(COUNT(*) <= 1, ANY_VALUE(CITY_NAME), NULL)",
      "type": "String"
    }
  }`,
  [Provider.Redshift]: `{
    "population_sum": {
      "formula": "SUM(population)",
      "type": "Number"
    },
    "city_name": {
      "formula": "(CASE WHEN COUNT(*) <= 1 THEN ANY_VALUE(city_name) ELSE NULL END)",
      "type": "String"
    }
  }`,
  [Provider.Postgres]: `{
    "population_sum": {
      "formula": "SUM(population)",
      "type": "Number"
    },
    "city_name": {
      "formula": "(CASE WHEN COUNT(*) <= 1 THEN ANY_VALUE(city_name) ELSE NULL END)",
      "type": "String"
    }
  }`,
  [Provider.DatabricksRest]:
    'SUM(population) AS population_sum; (CASE WHEN COUNT(*) <= 1 THEN ANY_VALUE(city_name) ELSE NULL END) AS city_name'
}

const methods: Record<string, Map<string, string>> = {
  [Provider.BigQuery]: new Map([
    ['max', 'MAX($1)'],
    ['min', 'MIN($1)'],
    ['sum', 'SUM($1)'],
    ['count', 'COUNT($1)'],
    ['concat', 'STRING_AGG($1)'],
    ['avg', 'AVG($1)'],
    ['any', 'ANY_VALUE($1)'],
    ['array', 'ARRAY_AGG($1)']
  ]),
  [Provider.Snowflake]: new Map([
    ['max', 'MAX($1)'],
    ['min', 'MIN($1)'],
    ['sum', 'SUM($1)'],
    ['count', 'COUNT($1)'],
    ['concat', "LISTAGG($1, ',')"],
    ['avg', 'AVG($1)'],
    ['any', 'ANY_VALUE($1)'],
    ['array', 'ARRAY_AGG($1)']
  ]),
  [Provider.Postgres]: new Map([
    ['max', 'MAX($1)'],
    ['min', 'MIN($1)'],
    ['sum', 'SUM($1)'],
    ['count', 'COUNT($1)'],
    ['concat', "STRING_AGG($1, ',')"],
    ['avg', 'AVG($1)'],
    ['any', 'COALESCE($1)'],
    ['array', 'ARRAY_AGG($1)']
  ]),
  [Provider.Redshift]: new Map([
    ['max', 'MAX($1)'],
    ['min', 'MIN($1)'],
    ['sum', 'SUM($1)'],
    ['count', 'COUNT($1)'],
    ['concat', "LISTAGG(CAST($1 AS TEXT), ',')"],
    ['avg', 'AVG($1)'],
    ['any', 'ANY_VALUE($1)'],
    ['array', `JSON_PARSE('"[' || listagg($1, ',') || ']"')`]
  ]),
  [Provider.DatabricksRest]: new Map([
    ['max', 'MAX($1)'],
    ['min', 'MIN($1)'],
    ['sum', 'SUM($1)'],
    ['count', 'COUNT($1)'],
    ['concat', `CONCAT_WS(',', COLLECT_LIST($1))`],
    ['avg', 'AVG($1)'],
    ['any', 'ANY_VALUE($1)'],
    ['array', `ARRAY_AGG($1)`]
  ])
}

class CreatePointAggTileset extends WorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.createpointaggtileset',
        group: 'Tileset Creation',
        title: 'Create Point Agg Tileset',
        description: `
          This component creates a tileset from an input containing Points. It will aggregate points close to each other and their properties.
        `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'destination',
          type: WorkflowComponentParamType.OutputTable,
          title: 'Table details',
          placeholder: 'Rename and select destination'
        },
        {
          name: 'geom_column',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'zoom_min',
          type: WorkflowComponentParamType.Number,
          title: 'Minimum zoom level',
          min: 0,
          max: 18,
          mode: WorkflowComponentParameterMode.Slider,
          allowExpressions: false,
          default: 0
        },
        {
          name: 'zoom_max',
          type: WorkflowComponentParamType.Number,
          title: 'Maximum zoom level',
          min: 1,
          max: 20,
          mode: WorkflowComponentParameterMode.Slider,
          allowExpressions: false,
          default: 12
        },
        {
          name: 'aggregation_resolution',
          type: WorkflowComponentParamType.Number,
          title: 'Aggregation resolution',
          min: 1,
          max: 10,
          mode: WorkflowComponentParameterMode.Slider,
          default: 6,
          allowExpressions: false,
          helper: 'workflows.parameterForm.pointAggAggregationResolutionHelper'
        },
        {
          name: 'aggregation_placement',
          type: WorkflowComponentParamType.Selection,
          title: 'Aggregation placement',
          options: [
            'cell-centroid',
            'cell',
            'features-any',
            'features-centroid'
          ],
          default: 'cell-centroid',
          allowExpressions: false,
          helper: 'workflows.parameterForm.pointAggAggregationPlacementHelper'
        },
        {
          name: 'properties',
          type: WorkflowComponentParamType.SelectColumnAggregation,
          allowExpression: false,
          title: 'Properties',
          placeholder: 'workflows.parameterForm.selectAField',
          parent: 'source',
          noDefault: true
        },
        {
          name: 'addcustomexpression',
          type: WorkflowComponentParamType.Boolean,
          title: 'Add custom expression',
          optional: true,
          default: false
        },
        {
          name: 'expression',
          type: WorkflowComponentParamType.Json,
          title: '',
          optional: true,
          default: '',
          placeholder: expressionPlaceholders,
          allowExpressions: false,
          showIf: [{ parameter: 'addcustomexpression', value: true }]
        },
        {
          name: 'zoom_min_col',
          title: 'Minimum zoom column',
          type: WorkflowComponentParamType.Column,
          parent: 'source',
          dataType: [ColumnDataType.Number, ColumnDataType.String],
          providers: [Provider.BigQuery],
          default: '',
          advanced: true,
          optional: true,
          allowExpressions: false,
          helper: 'workflows.parameterForm.pointAggZoomMinColHelper'
        },
        {
          name: 'zoom_max_col',
          title: 'Maximum zoom column',
          type: WorkflowComponentParamType.Column,
          parent: 'source',
          dataType: [ColumnDataType.Number, ColumnDataType.String],
          providers: [Provider.BigQuery],
          default: '',
          advanced: true,
          optional: true,
          allowExpressions: false,
          helper: 'workflows.parameterForm.pointAggZoomMaxColHelper'
        },
        {
          name: 'max_tile_features',
          type: WorkflowComponentParamType.Number,
          title: 'Maximum features per tile',
          min: 0,
          providers: [
            Provider.BigQuery,
            Provider.Snowflake,
            Provider.Redshift,
            Provider.Postgres
          ],
          allowExpressions: false,
          optional: true,
          advanced: true
        },
        {
          name: 'max_tile_size_kb',
          type: WorkflowComponentParamType.Number,
          title: 'Maximum kilobytes per tile',
          min: 1,
          max: 6 * 1024,
          default: 1024, // FIXME: Bug # 425167
          allowExpressions: false,
          providers: [Provider.BigQuery, Provider.Postgres],
          optional: true,
          advanced: true
        },
        {
          name: 'tile_feature_order',
          type: WorkflowComponentParamType.String,
          title: 'Tile feature order',
          placeholder: 'E.g.: ST_AREA(geom) DESC',
          default: '',
          providers: [
            Provider.BigQuery,
            Provider.Snowflake,
            Provider.Redshift,
            Provider.Postgres
          ],
          optional: true,
          advanced: true,
          allowExpressions: false,
          helper: 'workflows.parameterForm.tileFeatureOrderHelper'
        },
        {
          name: 'metadata',
          type: WorkflowComponentParamType.Json,
          title: 'Metadata',
          placeholder: 'E.g.: { "name": "...", "description": "..." }',
          default: '',
          allowExpressions: false,
          optional: true,
          advanced: true
        },
        {
          name: 'max_categories_limit',
          type: WorkflowComponentParamType.Number,
          title: 'Max categories generated',
          min: 1,
          allowExpressions: false,
          optional: true,
          advanced: true,
          requiredAnalyticsToolboxVersion: {
            [Provider.BigQuery]: '1.22.2',
            [Provider.Snowflake]: '1.20.2',
            [Provider.Redshift]: '1.11.2',
            [Provider.Postgres]: '1.4.1'
          }
        }
      ],
      []
    )
  }

  /**
   * Generates a SQL command to the CREATE_POINT_AGGREGATION_TILESET prodecure for the selected
   * provider.
   * This function supports both dry run and actual execution modes.
   *
   * @param {WorkflowComponentSQLParams} params - The parameters needed to generate the SQL command.
   * @returns {Promise<QueryAndOutputs>} A promise that resolves to the SQL query and output settings.
   */
  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType,
    schemas
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const preparedParams = this._prepareParameters(params)
    const isDryRun = codeType === WorkflowCodeType.DryRun

    const propertiesTuples: string[] = (
      preparedParams.properties as string
    )?.split(',')
    let properties = connection.type === Provider.DatabricksRest ? '' : {}
    if (connection.type === Provider.DatabricksRest) {
      /* Takes the chosen properties/aggregation function pairs (eg. ["prop_a", "AVG", "prop_b", "SUM"]
       * and generates a valid property SQL with 'type' and 'formula' fields. Eg:
       *
       *     AVG(population) AS prop_a; SUM(age) AS prop_b
       *
       * */
      properties = propertiesTuples
        ?.reduce((acc: string[], key: string, idx: number) => {
          if (idx % 2 === 0 && propertiesTuples[idx + 1]) {
            // Check for identifier and aggregation function
            const formula =
              methods[connection.type]
                .get(propertiesTuples[idx + 1])
                ?.replace(/\$1/g, key) || ''
            acc.push(`${formula} AS ${key}`)
          }
          return acc
        }, [])
        .join('; ')

      // Handle custom expressions
      if (preparedParams.addcustomexpression) {
        const expressionProps = (preparedParams.expression as string) || ''
        const separator = properties ? '; ' : ''
        properties += separator + expressionProps // Spread within an explicitly new object
      }
    } else {
      /* Takes the chosen properties/aggregation function pairs (eg. ["prop_a", "AVG", "prop_b", "SUM"]
       * and generates a valid property object with 'type' and 'formula' fields. Eg:
       *
       *     {
       *         prop_a: {
       *            "type": "Number",
       *            "formula": "AVG(population)"
       *         },
       *         prop_b: {
       *            "type": "Number",
       *            "formula": "SUM(age)"
       *         },
       *     }
       *
       * */
      properties = propertiesTuples?.reduce((acc, key, idx) => {
        if (idx % 2 === 0) {
          const schema = schemas?.input.source as TableSchema
          let type = schema?.find((item) => item.name === key)?.type as string
          type = type ? type.charAt(0).toUpperCase() + type.slice(1) : type
          const formula =
            methods[connection.type]
              .get(propertiesTuples[idx + 1])
              ?.replace(/\$1/g, key) || ''
          acc[key] = { type, formula }
        }
        return acc
      }, {} as Record<string, { type: string | undefined; formula: string }>)
      // Handle custom expressions
      if (preparedParams.addcustomexpression) {
        const expressionProps = JSON.parse(
          (preparedParams.expression as string) || '{}'
        )
        properties = { ...properties, ...expressionProps } // Spread within an explicitly new object
      }
    }

    const options: Record<string, unknown> = {
      zoom_min: preparedParams.zoom_min,
      zoom_max: preparedParams.zoom_max,
      aggregation_resolution: preparedParams.aggregation_resolution,
      aggregation_placement: preparedParams.aggregation_placement,
      geom_column: preparedParams.geom_column,
      properties: properties,
      metadata: preparedParams.metadata
        ? JSON.parse(preparedParams.metadata as string)
        : {},
      max_categories_limit: preparedParams.max_categories_limit
    }

    switch (connection.type) {
      case Provider.DatabricksRest:
        options['if_exists'] = 'replace'
        break
      default: // ORDER BY 1 functionally identical to no ORDER BY clause
        options['max_tile_size_strategy'] = 'drop_fraction_as_needed'
        options['max_tile_features'] = preparedParams.max_tile_features
        options['tile_feature_order'] =
          preparedParams.tile_feature_order ||
          (connection.type === Provider.BigQuery ? '(SELECT 1)' : '')

        break
    }

    // Remove any null/empty options
    Object.keys(options).forEach(
      (key) =>
        (options[key] == null || options[key] == '') && delete options[key]
    )

    const quotedSource = connection.quoteName(preparedParams.source as string)
    const quotedDestination = connection.quoteName(
      preparedParams.destination as string
    )
    const quotedJsonOptions =
      connection.type === Provider.DatabricksRest
        ? `'${JSON.stringify(options)}'`
        : connection.quoteMultilineString(JSON.stringify(options))

    let query: string
    if (isDryRun) {
      if (connection.type === Provider.BigQuery) {
        query = connection.getCreateTempTableQuery(
          quotedDestination,
          'SELECT 0 AS z, 0 AS x, 0 AS y, 0 AS carto_partition, B"abc" AS data',
          codeType,
          null
        )
      } else {
        query = connection.getCreateTempTableQuery(
          quotedDestination,
          'SELECT 0 AS z, 0 AS x, 0 AS y, CAST(NULL AS BINARY) AS data',
          codeType,
          null
        )
        if (connection.type === Provider.DatabricksRest) {
          query = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(
            query
          )
        }
      }
    } else {
      switch (connection.type) {
        case Provider.BigQuery:
        case Provider.Snowflake:
        case Provider.Postgres:
          query = dedent`
          ${connection.getDropTableQuery(quotedDestination)}
          CALL ${connection.quoteName(
            envVars.analyticsToolboxDataset as string
          )}.CREATE_POINT_AGGREGATION_TILESET(
            '${quotedSource}',
            '${quotedDestination}',
            ${quotedJsonOptions}
          );
        `
          break
        case Provider.Redshift:
          // Redshift needs destination as variable, not hardcoded string
          query = dedent`
          DECLARE rsDestTable TEXT;
          BEGIN
          rsDestTable := '${quotedDestination}';
          EXECUTE 'DROP TABLE IF EXISTS ' || rsDestTable || ' CASCADE;';
          CALL ${connection.quoteName(
            envVars.analyticsToolboxDataset as string
          )}.CREATE_POINT_AGGREGATION_TILESET(
            '${quotedSource}',
            rsDestTable,
            ${quotedJsonOptions}
          );
          END;
        `
          break
        case Provider.DatabricksRest:
          query = `
spark.read.format("com.carto.analytics.toolbox.stored.procedures.ATStoredProcedureDatasource").option(
      "sql",
      """
      CALL_CARTO carto_un.carto.CREATE_POINT_AGG_TILESET(

        '${quotedSource}',
        '${quotedDestination}',
        ${quotedJsonOptions}
        );
      """
    ).load().collect();`
          break
        default:
          return null
      }
    }

    // Create a dummy table to be able to retrieve the status of the node.
    const tablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      SINGLE_OUTPUT_NAME,
      codeType
    )
    let createTable = connection.getCreateTempTableQuery(
      tablename,
      `SELECT 1 AS N`,
      codeType,
      null
    )
    if (connection.type === Provider.DatabricksRest) {
      createTable = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(
        createTable
      )
    }
    query += `\n${createTable}`

    return {
      outputs: {
        result: connection.applyCapitalization(
          preparedParams.destination as string
        )
      },
      query
    }
  }

  getOutputTables({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentOutputTablesParams): Record<string, string> {
    const tablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      SINGLE_OUTPUT_NAME,
      codeType
    )
    return {
      [SINGLE_OUTPUT_NAME]: tablename
    }
  }

  getUIProperties(provider?: Provider): Record<string, unknown> {
    const classifiedMethods: Record<string, string[]> = {
      [ColumnDataType.Number]: provider
        ? Array.from(methods[provider].keys())
        : [],
      [ColumnDataType.String]: provider
        ? Array.from(methods[provider].keys())
        : []
    }
    return { methods: classifiedMethods }
  }

  language(provider: Provider): Language {
    if (provider === Provider.DatabricksRest) {
      return Language.Python
    }
    return Language.SQL
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    return '1.0.0'
  }

  getVersion(provider: Provider) {
    return '1'
  }
  supportedProviders(): Provider[] {
    return [
      Provider.BigQuery,
      Provider.Postgres,
      Provider.Redshift,
      Provider.Snowflake,
      Provider.DatabricksRest
    ]
  }
}

export { CreatePointAggTileset }
